import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiPost, ApiError } from '../lib/apiClient';
import { useModal } from '../contexts/ModalContext';

interface RefundResult {
  success: boolean;
  [key: string]: unknown;
}

export function PaymentsFinancePage() {
  const { confirm } = useModal();
  const [paymentId, setPaymentId] = useState('');
  const [amountMinor, setAmountMinor] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RefundResult | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const ok = await confirm({
      title: 'Issue refund',
      message: `Issue a refund for payment ${paymentId}? This calls Stripe immediately.`,
      confirmLabel: 'Issue refund',
      danger: true,
    });
    if (!ok) return;

    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const body: Record<string, unknown> = { reason };
      if (amountMinor.trim()) body.amountMinor = Number(amountMinor);
      const res = await apiPost<RefundResult>(`/payments/${encodeURIComponent(paymentId)}/refund`, body);
      setResult(res);
      setPaymentId('');
      setAmountMinor('');
      setReason('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Refund failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Payments &amp; Finance</h1>
          <p>
            Issue a Stripe refund. Full refunds reverse the specialist's transfer and application fee and void the
            case's earnings record. See also{' '}
            <Link to="/trust-safety">Trust &amp; Safety flags</Link> for chargeback/refund-frequency signals.
          </p>
        </div>
      </div>

      <form className="form-card" onSubmit={handleSubmit}>
        <label htmlFor="paymentId">Payment ID</label>
        <input id="paymentId" required value={paymentId} onChange={(e) => setPaymentId(e.target.value)} />

        <label htmlFor="amountMinor">Amount to refund (minor units — cents/pesos)</label>
        <input
          id="amountMinor"
          type="number"
          min={1}
          placeholder="Leave blank for a full refund"
          value={amountMinor}
          onChange={(e) => setAmountMinor(e.target.value)}
        />
        <p className="form-hint">Omit for a full refund of the remaining balance.</p>

        <label htmlFor="reason">Reason</label>
        <textarea
          id="reason"
          required
          rows={3}
          maxLength={200}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. specialist_abandoned_case"
        />

        {error && <p className="auth-error" role="alert">{error}</p>}

        <button className="btn btn-primary" type="submit" disabled={submitting}>
          {submitting ? 'Issuing refund…' : 'Issue refund'}
        </button>
      </form>

      {result && (
        <div className="result-card">
          Refund issued. <pre style={{ whiteSpace: 'pre-wrap', margin: '0.5rem 0 0' }}>{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}
